import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {AuthService} from './auth.service';
import {CommonService} from '../services/common.service';
import * as AuthActions from './auth.actions';
import {catchError, map, mergeMap, timeout, filter, take} from 'rxjs/operators';
import {of, tap} from 'rxjs';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  private router = inject(Router);
  private store = inject(Store);

    login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({email, password}) =>
        this.authService.login({email, password}).pipe(
          timeout(30000), // 30 second timeout
          map((response: any) => {
            if (response?.data?.profile && response?.data?.token) {
              try {
                // Prepare query params
                const params: any = {};
                if (response.data.token) {
                  params.token = response.data.token;
                }
                if (response.data['refreshToken']) {
                  params['refresh-token'] = response.data['refresh-token'];
                }
                if (response.data.profile) {
                  const profileString = JSON.stringify(response.data.profile);
                  params.profile = btoa(unescape(encodeURIComponent(profileString)));
                }
                // Build query string
                const queryString = Object.keys(params)
                  .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
                  .join('&');
                  const queryParams: any = {};
                  if (queryString) {
                    queryString.split('&').forEach(param => {
                      const [key, value] = param.split('=');
                      if (key && value) {
                        queryParams[decodeURIComponent(key)] = decodeURIComponent(value);
                      }
                    });
                  }
                  this.commonService.setItemToLocalStorage('neuralops/query-params', JSON.stringify(queryParams));

                const url = `https://app.neuralops.co${queryString ? '?' + queryString : ''}`;
                window.open(url, '_self');
              } catch (error) {
                // Silent error handling for setProfile
              }
            } else if (!response?.data?.profile && response?.data?.token) {
              try {
                this.commonService.setItemToLocalStorage('accesshub/verify-email-token', response?.data?.token);
                this.authService.verifyEmailOnLogin.next(null);
              } catch (error) {
                // Silent error handling for email verification
              }
            }

            return AuthActions.loginSuccess();
          }),
          catchError(err => {
            // Handle timeout specifically
            if (err.name === 'TimeoutError') {
              this.commonService.notification('Login request timed out. Please try again.', 'danger');
            } else if (err.error && Array.isArray(err.error.errors)) {
              this.commonService.displayErrorsWithDelay(err.error.errors, 'danger');
            } else {
              this.commonService.notification(
                err.error?.message || err.message || 'Login failed',
                'danger'
              );
            }

            return of(AuthActions.loginFailure());
          })
        )
      )
    );
  });

  signup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.signup),
      mergeMap(({email, password, confirmPassword}) =>
        this.authService.signup({email, password, confirmPassword}).pipe(
          map((response: any) => {
            if (response?.data?.token) {
              this.commonService.setItemToLocalStorage('accesshub/verify-email-token', response?.data?.token);
              this.commonService.setSignupSuccess(true);
              this.commonService.notification(
                response.message,
                'success'
              );
            }

            return AuthActions.signupSuccess();
          }),
          catchError(response => {
            if (response.error && Array.isArray(response.error.errors)) {
              this.commonService.displayErrorsWithDelay(response.error.errors, 'danger');
            } else {
              this.commonService.notification(
                response.error?.message || response.message || 'Signup failed',
                'danger'
              );
            }
            return of(AuthActions.signupFailure());
          })
        )
      )
    );
  });

  verifyEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.verifyEmail),
      mergeMap(({otp}) =>
        this.authService.verifyEmail({otp}).pipe(
          map((response: any) => {
            // Prepare query params and open URL as in login
            if (response?.data?.profile && response?.data?.token) {
              try {
                const params: any = {};
                if (response.data.token) {
                  params.token = response.data.token;
                }
                if (response.data['refresh-token']) {
                  params['refresh-token'] = response.data['refresh-token'];
                }
                if (response.data.profile) {
                  const profileString = JSON.stringify(response.data.profile);
                  params.profile = btoa(unescape(encodeURIComponent(profileString)));
                }
                const queryString = Object.keys(params)
                  .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
                  .join('&');
                  // Parse queryString and store individual params as JSON
                  const queryParams: any = {};
                  if (queryString) {
                    queryString.split('&').forEach(param => {
                      const [key, value] = param.split('=');
                      if (key && value) {
                        queryParams[decodeURIComponent(key)] = decodeURIComponent(value);
                      }
                    });
                  }
                  this.commonService.setItemToLocalStorage('neuralops/query-params', JSON.stringify(queryParams));
                  
                  const url = `/downloads`;
                window.open(url, '_self');

                this.commonService.setVerifyEmailSuccess(true);
              } catch (error) {
                // Silent error handling for setProfile
              }
            }

            this.commonService.notification(response.message, 'success');

            return AuthActions.verifyEmailSuccess();
          }),
          catchError(response => {
            if (response?.error && Array.isArray(response?.error?.errors)) {
              this.commonService.displayErrorsWithDelay(response?.error?.errors, 'danger');
            } else {
              this.commonService.notification(
                response.error?.message || response?.message || 'Email verification failed',
                'danger'
              );
            }
            return of(AuthActions.verifyEmailFailure());
          }),
          tap((action) => {
            if (action.type === AuthActions.verifyEmailSuccess.type) {
              this.router.navigate(['/']).then(() => {
                this.commonService.setSignupSuccess(false);
                this.commonService.removeItemFromLocalStorage('accesshub/verify-email-token');
              });
            }
          })
        )
      )
    );
  });

  forgotPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      mergeMap(({email}) =>
        this.authService.forgotPassword({email}).pipe(
          map((response: any) => {
            if (response?.data?.token) {
              this.commonService.setItemToLocalStorage('accesshub/reset-password-token', response.data.token);
              this.commonService.setForgotPasswordSuccess(true);
            }

            this.commonService.notification(response.message, 'success');

            return AuthActions.forgotPasswordSuccess();
          }),
          catchError(response => {
            if (response?.error && Array.isArray(response?.error?.errors)) {
              this.commonService.displayErrorsWithDelay(response?.error?.errors, 'danger');
            } else {
              this.commonService.notification(
                response.error?.message || response?.message || 'Password reset request failed',
                'danger'
              );
            }
            return of(AuthActions.forgotPasswordFailure());
          })
        )
      )
    );
  });

  resetPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      mergeMap(({otp, password, confirmPassword}) =>
        this.authService.resetPassword({otp, password, confirmPassword}).pipe(
          map((response: any) => {
            this.commonService.notification(
              response.message,
              'success'
            );

            this.commonService.removeItemFromLocalStorage('accesshub/reset-password-token');
            this.commonService.setForgotPasswordSuccess(false);

            return AuthActions.resetPasswordSuccess();
          }),
          catchError(response => {
            if (response?.error && Array.isArray(response?.error?.errors)) {
              this.commonService.displayErrorsWithDelay(response?.error?.errors, 'danger');
            } else {
              this.commonService.notification(
                response.error?.message || response?.message || 'Password reset failed',
                'danger'
              );
            }
            return of(AuthActions.resetPasswordFailure());
          }),
          tap((action) => {
            if (action.type === AuthActions.resetPasswordSuccess.type) {
              this.commonService.removeItemFromLocalStorage('accesshub/reset-password-token');
              this.commonService.setForgotPasswordSuccess(false);
              window.open('https://neuralops.co/login', '_self');
            }
          })
        )
      )
    );
  });

  resendOtp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.resendOtp),
      mergeMap((action) =>
        this.authService.resendOtp(action.payload).pipe(
          map((response: any) => {
            this.commonService.notification(
              response.message || 'OTP has been resent successfully',
              'success'
            );
            return AuthActions.resendOtpSuccess();
          }),
          catchError(response => {
            if (response?.error && Array.isArray(response?.error?.errors)) {
              this.commonService.displayErrorsWithDelay(response?.error?.errors, 'danger');
            } else {
              this.commonService.notification(
                response.error?.message || response?.message || 'Failed to resend OTP',
                'danger'
              );
            }
            return of(AuthActions.resendOtpFailure());
          })
        )
      )
    );
  });

  validatePasswordResetOtp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.validatePasswordResetOtp),
      mergeMap(({email, otp}) =>
        this.authService.validatePasswordResetOtp({email, otp}).pipe(
          map((response: any) => {
            // Store token in localStorage if provided
            if (response?.data?.token) {
              this.commonService.setItemToLocalStorage('accesshub/reset-password-token', response.data.token);
            }

            // Show success notification
            this.commonService.notification(
              response.message || 'OTP validated successfully',
              'success'
            );

            // Emit success to Subject
            this.authService.passwordResetOtpValidationResult.next({success: true});

            return AuthActions.validatePasswordResetOtpSuccess();
          }),
          catchError(response => {
            let errorMessage = 'OTP validation failed';

            // Handle errors and emit to Subject
            if (response?.error && Array.isArray(response?.error?.errors)) {
              this.commonService.displayErrorsWithDelay(response?.error?.errors, 'danger');
              errorMessage = response.error.errors[0]?.message || errorMessage;
              this.authService.passwordResetOtpValidationResult.next({
                success: false,
                error: errorMessage
              });
            } else {
              errorMessage = response.error?.message || response?.message || errorMessage;
              this.commonService.notification(
                errorMessage,
                'danger'
              );
              this.authService.passwordResetOtpValidationResult.next({
                success: false,
                error: errorMessage
              });
            }

            return of(AuthActions.validatePasswordResetOtpFailure());
          })
        )
      )
    );
  });
}
